import { activeMeasurementIdAtom, addMeasurementAtom, addPointModeAtom, updateMeasurementPointsAtom } from '@/atoms';
import { PointCloudOctree } from '@pnext/three-loader';
import { Text } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import PointX from './PointX';

/** Slim cylinder drawn between two world-space points. */
const CylinderLine = ({
  start,
  end,
  color,
  opacity = 0.8,
  radius = 0.005,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  opacity?: number;
  radius?: number;
}) => {
  const { midpoint, length, quaternion } = useMemo(() => {
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const len = start.distanceTo(end);
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return {
      midpoint: mid.toArray() as [number, number, number],
      length: len,
      quaternion: [quat.x, quat.y, quat.z, quat.w] as [number, number, number, number],
    };
  }, [start, end]);

  if (length < 1e-6) return null;

  return (
    <mesh position={midpoint} quaternion={quaternion} renderOrder={Number.MAX_SAFE_INTEGER - 100}>
      <cylinderGeometry args={[radius, radius, length, 8, 1]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </mesh>
  );
};

/**
 * Simple fan triangulation from vertex 0.
 */
const fanTriangulate = (count: number): number[][] => {
  const tris: number[][] = [];
  for (let i = 1; i < count - 1; i++) {
    tris.push([0, i, i + 1]);
  }
  return tris;
};

/**
 * Compute the true 3D area of a polygon using the cross-product summation
 * method (generalised Newell). Works for any orientation.
 */
const computePolygonArea = (points: THREE.Vector3[]): number => {
  if (points.length < 3) return 0;
  const sum = new THREE.Vector3();
  const cross = new THREE.Vector3();
  for (let i = 0; i < points.length; i++) {
    cross.crossVectors(points[i], points[(i + 1) % points.length]);
    sum.add(cross);
  }
  return sum.length() * 0.5;
};

/** Compute the centroid of a polygon. */
const computeCentroid = (points: THREE.Vector3[]): THREE.Vector3 => {
  const centroid = new THREE.Vector3();
  for (const p of points) centroid.add(p);
  centroid.divideScalar(points.length);
  return centroid;
};

const CLOSE_THRESHOLD_PX = 20;

const AreaMeasuringTool = ({ targets, projectId }: { targets: PointCloudOctree[]; projectId?: string }) => {
  const [pickedPoints, setPickedPoints] = useState<THREE.Vector3[]>([]);
  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [isClosed, setIsClosed] = useState(false);
  const [isNearFirstPoint, setIsNearFirstPoint] = useState(false);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const [params] = useSearchParams();
  const showAreaMeasurement = params.get('showAreaMeasurement') === 'true';
  const addPointMode = useAtomValue(addPointModeAtom);
  const addMeasurement = useSetAtom(addMeasurementAtom(projectId ?? ''));
  const updateMeasurementPoints = useSetAtom(updateMeasurementPointsAtom(projectId ?? ''));
  const setActiveMeasurementId = useSetAtom(activeMeasurementIdAtom);
  const savedIdRef = useRef<string | null>(null);

  const saveMeasurement = useCallback(
    (points: THREE.Vector3[]) => {
      if (!projectId || points.length < 3) return;
      const id = addMeasurement({
        type: 'area',
        points: points.map((p) => p.toArray() as [number, number, number]),
      });
      savedIdRef.current = id ?? null;
      setActiveMeasurementId(id ?? null);
    },
    [projectId, addMeasurement],
  );

  const draggingRef = useRef<{
    index: number;
    plane: THREE.Plane;
    offset: THREE.Vector3;
  } | null>(null);

  // Helper: screen-space distance from mouse to a world point
  const getScreenDistance = (clientX: number, clientY: number, worldPoint: THREE.Vector3) => {
    const rect = renderer.domElement.getBoundingClientRect();
    const projected = worldPoint.clone().project(camera);
    const sx = ((projected.x + 1) / 2) * rect.width;
    const sy = ((-projected.y + 1) / 2) * rect.height;
    return Math.sqrt((clientX - rect.left - sx) ** 2 + (clientY - rect.top - sy) ** 2);
  };

  // Hover handler
  useEffect(() => {
    if (!showAreaMeasurement || addPointMode?.enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isClosed) {
        renderer.domElement.style.cursor = 'default';
        return;
      }

      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0 && hits[0]?.position) {
        setHoverPoint(hits[0].position.clone());
        renderer.domElement.style.cursor = 'crosshair';

        if (pickedPoints.length >= 3) {
          const dist = getScreenDistance(e.clientX, e.clientY, pickedPoints[0]);
          setIsNearFirstPoint(dist < CLOSE_THRESHOLD_PX);
        } else {
          setIsNearFirstPoint(false);
        }
      } else {
        setHoverPoint(null);
        setIsNearFirstPoint(false);
        renderer.domElement.style.cursor = 'default';
      }
    };

    renderer.domElement.addEventListener('pointermove', handleMouseMove);
    return () => {
      renderer.domElement.removeEventListener('pointermove', handleMouseMove);
      renderer.domElement.style.cursor = 'default';
    };
  }, [showAreaMeasurement, addPointMode, targets, camera, renderer, raycaster, pickedPoints, isClosed]);

  // Click handler — track pointer down/up to distinguish clicks from camera drags
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!showAreaMeasurement) return;
    const dom = renderer.domElement;
    const DRAG_THRESHOLD = 5;

    const handlePointerDown = (e: MouseEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
    };

    const handlePointerUp = (e: MouseEvent) => {
      // Only react to left-click
      if (e.button !== 0) return;
      // Ignore if this was a camera drag, not a click
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerDownPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) return;

      if (addPointMode?.enabled) return;
      if (draggingRef.current) return;

      // If polygon is already closed, clear and start new
      if (isClosed) {
        hasSavedRef.current = false;
        savedIdRef.current = null;
        setActiveMeasurementId(null);
        setPickedPoints([]);
        setIsClosed(false);
        setIsNearFirstPoint(false);
        setHoverPoint(null);
        return;
      }

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length === 0 || !hits[0]?.position) return;

      // Check if we should close the polygon
      if (pickedPoints.length >= 3) {
        const dist = getScreenDistance(e.clientX, e.clientY, pickedPoints[0]);
        if (dist < CLOSE_THRESHOLD_PX) {
          setIsClosed(true);
          setHoverPoint(null);
          setIsNearFirstPoint(false);
          return;
        }
      }

      const world = new THREE.Vector3(...hits[0].position.toArray());
      setPickedPoints((prev) => [...prev, world]);
      setHoverPoint(null);
    };

    dom.addEventListener('pointerdown', handlePointerDown);
    dom.addEventListener('pointerup', handlePointerUp);
    return () => {
      dom.removeEventListener('pointerdown', handlePointerDown);
      dom.removeEventListener('pointerup', handlePointerUp);
    };
  }, [targets, camera, renderer, raycaster, addPointMode, showAreaMeasurement, pickedPoints, isClosed]);

  // Save once when polygon is first closed (not on subsequent point drags)
  const hasSavedRef = useRef(false);
  useEffect(() => {
    if (isClosed && pickedPoints.length >= 3 && !hasSavedRef.current) {
      hasSavedRef.current = true;
      saveMeasurement(pickedPoints);
    }
  }, [isClosed, pickedPoints, saveMeasurement]);

  // Reset when tool is disabled
  useEffect(() => {
    if (!showAreaMeasurement) {
      hasSavedRef.current = false;
      savedIdRef.current = null;
      setActiveMeasurementId(null);
      setPickedPoints([]);
      setHoverPoint(null);
      setIsClosed(false);
      setIsNearFirstPoint(false);
    }
  }, [showAreaMeasurement]);

  // Right-click cancels the current measurement
  useEffect(() => {
    if (!showAreaMeasurement) return;
    const dom = renderer.domElement;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      hasSavedRef.current = false;
      savedIdRef.current = null;
      setActiveMeasurementId(null);
      setPickedPoints([]);
      setHoverPoint(null);
      setIsClosed(false);
      setIsNearFirstPoint(false);
    };

    dom.addEventListener('contextmenu', handleContextMenu);
    return () => {
      dom.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [showAreaMeasurement, renderer]);

  // Drag handler (only after polygon is closed)
  const startDrag = (e: ThreeEvent<PointerEvent>, index: number) => {
    if (!isClosed) return;
    e.stopPropagation();
    const point = e.point.clone();
    const camDir = new THREE.Vector3().copy(camera.getWorldDirection(new THREE.Vector3()));
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    const offset = point.clone().sub(pickedPoints[index]);
    draggingRef.current = { index, plane, offset };

    const dom = renderer.domElement;

    const onPointerMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(plane, intersectPoint)) {
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = intersectPoint.clone().sub(offset);
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: pts.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return pts;
        });
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerUp);

      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);
      if (hits.length > 0 && hits[0]?.position) {
        const world = new THREE.Vector3(...hits[0].position.toArray());
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = world;
          // Sync updated points to the saved measurement
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: pts.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return pts;
        });
      } else if (savedIdRef.current) {
        // No point cloud hit — still sync the plane-intersected position
        setPickedPoints((prev) => {
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: prev.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return prev;
        });
      }

      draggingRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp);
  };

  const area = useMemo(() => (isClosed ? computePolygonArea(pickedPoints) : 0), [pickedPoints, isClosed]);

  const centroid = useMemo(
    () => (pickedPoints.length >= 3 ? computeCentroid(pickedPoints) : new THREE.Vector3()),
    [pickedPoints],
  );

  const calculateScale = (point: THREE.Vector3) => {
    const d = camera.position.distanceTo(point);
    const pr = renderer.getPixelRatio();
    const height = renderer.domElement.clientHeight * pr;

    if (camera.type === 'OrthographicCamera') {
      const orthographicCamera = camera as THREE.OrthographicCamera;
      const worldScreenHeight = Math.abs(orthographicCamera.top - orthographicCamera.bottom) / orthographicCamera.zoom;
      const unitsPerPixel = worldScreenHeight / height;
      return 20 * unitsPerPixel;
    } else {
      const perspectiveCamera = camera as THREE.PerspectiveCamera;
      const vFOV = (perspectiveCamera.getEffectiveFOV() * Math.PI) / 180;
      const worldScreenHeight = 2 * d * Math.tan(vFOV / 2);
      const unitsPerPixel = worldScreenHeight / height;
      return 20 * unitsPerPixel;
    }
  };

  const scales = useMemo(() => pickedPoints.map(calculateScale), [pickedPoints, camera, renderer]);
  const hoverScale = useMemo(() => (hoverPoint ? calculateScale(hoverPoint) : 1), [hoverPoint, camera, renderer]);

  const textRotation = useMemo((): [number, number, number, number] => {
    if (camera.type === 'OrthographicCamera') return [0, 0, 0, 1];
    const q = camera.quaternion;
    return [q.x, q.y, q.z, q.w];
  }, [centroid, camera]);

  const fontSize = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    return centroid.distanceTo(camera.position) / 50;
  }, [centroid, pickedPoints, camera]);

  // Edge data
  const edges = useMemo(() => {
    const result: {
      start: THREE.Vector3;
      end: THREE.Vector3;
      distance: number;
      midpoint: [number, number, number];
    }[] = [];
    const n = isClosed ? pickedPoints.length : pickedPoints.length - 1;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % pickedPoints.length;
      const start = pickedPoints[i];
      const end = pickedPoints[j];
      const mid = start.clone().add(end).multiplyScalar(0.5);
      result.push({
        start,
        end,
        distance: start.distanceTo(end),
        midpoint: mid.toArray() as [number, number, number],
      });
    }
    return result;
  }, [pickedPoints, isClosed]);

  // Polygon fill geometry — simple fan triangulation (works for any orientation)
  const fillGeometry = useMemo(() => {
    if (!isClosed || pickedPoints.length < 3) return null;

    const triangles = fanTriangulate(pickedPoints.length);
    if (triangles.length === 0) return null;

    const positions = new Float32Array(triangles.length * 3 * 3);
    for (let i = 0; i < triangles.length; i++) {
      const [a, b, c] = triangles[i];
      positions[i * 9 + 0] = pickedPoints[a].x;
      positions[i * 9 + 1] = pickedPoints[a].y;
      positions[i * 9 + 2] = pickedPoints[a].z;
      positions[i * 9 + 3] = pickedPoints[b].x;
      positions[i * 9 + 4] = pickedPoints[b].y;
      positions[i * 9 + 5] = pickedPoints[b].z;
      positions[i * 9 + 6] = pickedPoints[c].x;
      positions[i * 9 + 7] = pickedPoints[c].y;
      positions[i * 9 + 8] = pickedPoints[c].z;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    return geometry;
  }, [pickedPoints, isClosed]);

  if (!showAreaMeasurement) return null;

  return (
    <>
      {/* Hover preview marker */}
      {hoverPoint && !draggingRef.current && !isClosed && <PointX scale={hoverScale} position={hoverPoint} />}

      {/* Close indicator - green sphere on first point */}
      {isNearFirstPoint && pickedPoints.length >= 3 && !isClosed && (
        <mesh position={pickedPoints[0].toArray()} renderOrder={Number.MAX_SAFE_INTEGER}>
          <sphereGeometry args={[scales[0] * 0.4, 16, 16]} />
          <meshBasicMaterial color='lime' transparent opacity={0.6} depthTest={false} />
        </mesh>
      )}

      {/* Vertex markers */}
      {pickedPoints.map((point, i) => (
        <PointX key={i} scale={scales[i]} position={point} onPointerDown={(e) => startDrag(e, i)} />
      ))}

      {/* Edge lines */}
      {edges.map((edge, i) => (
        <CylinderLine key={`edge-${i}`} start={edge.start} end={edge.end} color='orange' />
      ))}

      {/* Preview line from last point to hover point (or to first point if near) */}
      {!isClosed && pickedPoints.length > 0 && hoverPoint && !draggingRef.current && (
        <>
          <CylinderLine
            start={pickedPoints[pickedPoints.length - 1]}
            end={isNearFirstPoint ? pickedPoints[0] : hoverPoint}
            color='orange'
            opacity={0.5}
          />
          {/* Preview closing line from hover back to first point */}
          {pickedPoints.length >= 2 && !isNearFirstPoint && (
            <CylinderLine start={hoverPoint} end={pickedPoints[0]} color='orange' opacity={0.3} />
          )}
        </>
      )}

      {/* Polygon fill */}
      {fillGeometry && (
        <mesh geometry={fillGeometry} renderOrder={Number.MAX_SAFE_INTEGER - 200}>
          <meshBasicMaterial color='orange' transparent opacity={0.2} depthTest={false} side={THREE.DoubleSide} />
        </mesh>
      )}

      {/* Edge distance labels */}
      {!draggingRef.current &&
        edges.map((edge, i) => {
          const mid = new THREE.Vector3(...edge.midpoint);
          const edgeFontSize = mid.distanceTo(camera.position) / 50;
          return (
            <Text
              key={`label-${i}`}
              position={edge.midpoint}
              fontSize={edgeFontSize}
              color='white'
              outlineColor='black'
              outlineWidth={0.01}
              outlineBlur={0.01}
              strokeColor='orange'
              strokeWidth={0.001}
              depthOffset={Number.MIN_SAFE_INTEGER}
              renderOrder={Number.MAX_SAFE_INTEGER}
              anchorX='center'
              anchorY='middle'
              quaternion={textRotation}>
              {edge.distance.toFixed(2)} m
            </Text>
          );
        })}

      {/* Area label at centroid */}
      {isClosed && !draggingRef.current && (
        <Text
          position={centroid.toArray()}
          fontSize={fontSize * 1.2}
          color='white'
          outlineColor='black'
          outlineWidth={0.015}
          outlineBlur={0.015}
          strokeColor='orange'
          strokeWidth={0.002}
          depthOffset={Number.MIN_SAFE_INTEGER}
          renderOrder={Number.MAX_SAFE_INTEGER}
          anchorX='center'
          anchorY='middle'
          quaternion={textRotation}>
          {area.toFixed(2)} m²
        </Text>
      )}
    </>
  );
};

export default AreaMeasuringTool;
